import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { UserRole } from '../types';
import { isSubdomainEnabled, isOnAppSubdomain, isOnMainDomain, getAppDomain } from '../utils/domain';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search;

    // No subdomínio app.: login acontece aqui mesmo (não redirecionar para domínio principal)
    if (isSubdomainEnabled() && isOnAppSubdomain()) {
      const redirectParam = currentPath !== '/' ? `?redirect=${encodeURIComponent(currentPath)}` : '';
      return <Navigate to={`/login${redirectParam}`} replace />;
    }

    // No domínio principal: redirecionar para o subdomínio app. (onde o login acontece)
    if (isSubdomainEnabled() && isOnMainDomain()) {
      window.location.href = `https://${getAppDomain()}${currentPath}`;
      return null;
    }

    // Fallback (sem subdomain): salvar no localStorage e ir para /login
    if (currentPath !== '/') {
      localStorage.setItem('redirectAfterLogin', currentPath);
    }
    return <Navigate to="/auth" replace />;
  }

  // Usuário autenticado no domínio principal → redirecionar para o subdomínio app.
  if (isSubdomainEnabled() && isOnMainDomain()) {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `https://${getAppDomain()}${currentPath}`;
    return null;
  }

  // Se há roles requeridas, verificar se o usuário tem uma delas
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = user?.role && requiredRoles.includes(user.role);

    if (!hasRequiredRole) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
